import { BOARD_SIZE, type BoardSquare, type Seat, type SetupPlacement } from './schemas.js'

/**
 * The Duke's board-setup legality rules — the one place both the mock
 * server (as referee, `server/duke-api.store.ts`) and the client (as a
 * highlighter of legal squares, `features/game/setup-board.presenter.ts`)
 * decide what a legal placement is. Shared for the same reason `schemas.ts`
 * is: one validation both sides of the wire must agree on, not a layer of
 * the Black Box. No architecture test governs `src/shared/` — keeping this
 * as the sole implementation (never duplicated) is a review-level
 * discipline, not a mechanically enforced one.
 *
 * Pure functions only: no I/O, no state, nothing here decides anything about
 * a specific game beyond the board it is handed.
 */

// Host's home row is the last row (the bottom of the board), guest's is the
// first (the top) — fixed, never chosen, per the simplified simultaneous
// setup this app uses instead of the rulebook's side-choice-and-turn-order.
export const HOME_ROW: Readonly<Record<Seat, number>> = {
  host: BOARD_SIZE - 1,
  guest: 0,
}

// "places his Duke Tile in one of the two center squares in his edge row"
export const DUKE_COLUMNS: readonly number[] = [2, 3]

export type SetupRejection = 'duke-off-home-centre' | 'footman-not-orthogonal' | 'square-occupied' | 'duplicate-square'

export type SetupValidationResult =
  { kind: 'ok' } | { kind: 'rejected'; reason: SetupRejection; field: 'duke' | 'footmen' }

function isOccupied(square: BoardSquare, occupied: readonly BoardSquare[]): boolean {
  return occupied.some((s) => s.row === square.row && s.col === square.col)
}

function isSameSquare(a: BoardSquare, b: BoardSquare): boolean {
  return a.row === b.row && a.col === b.col
}

/** The two centre squares of `seat`'s home row that are not already occupied. */
export function legalDukeSquares(seat: Seat, occupied: readonly BoardSquare[]): readonly BoardSquare[] {
  const row = HOME_ROW[seat]
  return DUKE_COLUMNS.map((col) => ({ row, col })).filter((square) => !isOccupied(square, occupied))
}

/**
 * The Duke's side-sharing (orthogonal) neighbours, on-board and unoccupied.
 * "sharing only a corner is not allowed" — diagonals are never included, so
 * a Duke placed on an edge column yields 3 candidates, not 4.
 */
export function legalFootmanSquares(duke: BoardSquare, occupied: readonly BoardSquare[]): readonly BoardSquare[] {
  const candidates: BoardSquare[] = [
    { row: duke.row - 1, col: duke.col },
    { row: duke.row + 1, col: duke.col },
    { row: duke.row, col: duke.col - 1 },
    { row: duke.row, col: duke.col + 1 },
  ]
  return candidates.filter(
    (square) =>
      square.row >= 0 &&
      square.row < BOARD_SIZE &&
      square.col >= 0 &&
      square.col < BOARD_SIZE &&
      !isOccupied(square, occupied),
  )
}

/**
 * The full legality check for one seat's atomic setup submission, against
 * whatever is already on the board (the opponent's tiles, if they have set
 * up first). Order matters: the Duke is checked before either Footman, and
 * `duplicate-square` is only reachable once both Footmen are otherwise legal
 * individually.
 */
export function validateSetup(args: {
  seat: Seat
  placement: SetupPlacement
  occupied: readonly BoardSquare[]
}): SetupValidationResult {
  const { seat, placement, occupied } = args
  const { duke, footmen } = placement

  const legalDuke = legalDukeSquares(seat, occupied)
  if (!legalDuke.some((square) => isSameSquare(square, duke))) {
    return { kind: 'rejected', reason: 'duke-off-home-centre', field: 'duke' }
  }

  if (isSameSquare(footmen[0], footmen[1])) {
    return { kind: 'rejected', reason: 'duplicate-square', field: 'footmen' }
  }

  const legalFootman = legalFootmanSquares(duke, occupied)
  const bothLegal = footmen.every((square) => legalFootman.some((candidate) => isSameSquare(candidate, square)))
  if (!bothLegal) {
    // Distinguish "off the board / diagonal / off the home row's Duke" from
    // "on the board, orthogonal, but already occupied" so the server can
    // report the more specific reason.
    const anyOccupied = footmen.some((square) => isOccupied(square, occupied) && !isSameSquare(square, duke))
    return {
      kind: 'rejected',
      reason: anyOccupied ? 'square-occupied' : 'footman-not-orthogonal',
      field: 'footmen',
    }
  }

  return { kind: 'ok' }
}
